import React, { useEffect, useRef } from "react";

import { Button, InfoMessage, Spinner } from "@amphore-dev/amphore-lib";
import { AxiosError } from "axios";
import { t } from "i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useMutation } from "@tanstack/react-query";

import AuthService from "@/services/AuthService";

import { useAuth } from "@/hooks/useAuth";

import { getApiErrormessage } from "@/utils/UAPIErrors";

import { TApiError } from "@/types";

/**
 * /verify-email?token=: confirms the address as soon as the page opens,
 * signed in or not. Signed in, the banner goes at once (the user in
 * memory is updated); the invitations waiting for that address are
 * applied by the API on the way.
 */
export const VerifyEmail: React.FC = () => {
	const [params] = useSearchParams();
	const token = params.get("token") ?? "";
	const navigate = useNavigate();
	const { user, updateUser } = useAuth();
	const sent = useRef(false);

	const verify = useMutation<void, AxiosError<TApiError>, string>({
		mutationFn: (value) => AuthService.verifyEmail(value),
		onSuccess: () => {
			if (user) updateUser({ ...user, emailVerified: true });
		},
	});

	// Once: StrictMode mounts twice, and the link works only once.
	useEffect(() => {
		if (!token || sent.current) return;
		sent.current = true;
		verify.mutate(token);
	}, [token, verify]);

	if (!token)
		return (
			<InfoMessage color="danger">{t("account.invalidLink")}</InfoMessage>
		);

	if (verify.isPending || verify.isIdle)
		return <Spinner className="mx-auto my-6" />;

	return (
		<div className="flex flex-col gap-4" data-testid="verify-result">
			{verify.isSuccess ? (
				<InfoMessage color="success">
					{t("account.verify.done")}
				</InfoMessage>
			) : (
				<InfoMessage color="danger">
					{getApiErrormessage(verify.error)}
				</InfoMessage>
			)}
			<Button onClick={() => navigate(user ? "/" : "/login")}>
				{user ? t("account.verify.open") : t("auth.signIn")}
			</Button>
		</div>
	);
};
